import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  ChevronLeft,
  UserPlus,
  Mail,
  Home,
  User,
  Shield,
  Monitor,
  Calendar,
  ShoppingBag,
  Wrench,
  HelpCircle,
  Check,
  Send,
  AlertCircle,
  Clock,
  Info,
  ArrowRight,
  Loader2,
} from "lucide-react";
import { Sheet, SheetContent } from "../../components/ui/Sheet";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { cn } from "../../lib/utils";
import {
  inviteFamilyMember,
  HomeRole,
  ServiceType,
  ALL_SERVICE_TYPES,
  SERVICE_TYPE_LABELS,
} from "../../services/family.api";
import { getHomeAssets, Asset } from "../../services/asset.api";
import { useProperty } from "../../contexts/PropertyContext";

const TOTAL_STEPS = 4;

const SERVICE_ICONS: Record<ServiceType, React.ReactNode> = {
  maintenance: <Wrench className="h-5 w-5" />,
  warranty: <Shield className="h-5 w-5" />,
  bookings: <Calendar className="h-5 w-5" />,
  issues: <AlertCircle className="h-5 w-5" />,
  orders: <ShoppingBag className="h-5 w-5" />,
  protection_plans: <Shield className="h-5 w-5" />,
};

const SERVICE_DESCRIPTIONS: Record<ServiceType, string> = {
  maintenance: "View & create maintenance records",
  warranty: "View warranty plans & file claims",
  bookings: "Book home services",
  issues: "Report & view issues",
  orders: "Place orders for the home",
  protection_plans: "View protection plans",
};

const ROLE_OPTIONS: { value: HomeRole; label: string; desc: string }[] = [
  { value: "owner", label: "Co-Owner", desc: "Full access to everything" },
  { value: "member", label: "Member", desc: "Access to selected assets & services" },
  { value: "viewer", label: "Viewer", desc: "View-only access" },
];

const RELATIONS = [
  "Spouse", "Partner", "Child", "Parent", "Sibling",
  "Caretaker", "Tenant", "Other",
];

function roleLabel(role: HomeRole): string {
  switch (role) {
    case "owner": return "Co-Owner";
    case "member": return "Member";
    case "viewer": return "Viewer";
  }
}

interface InviteMemberDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function InviteMemberDrawer({ open, onOpenChange, onSuccess }: InviteMemberDrawerProps) {
  const { properties: allProperties, isLoading: propsLoading } = useProperty();

  const [currentStep, setCurrentStep] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [selectedRole, setSelectedRole] = useState<HomeRole>("member");
  const [selectedRelation, setSelectedRelation] = useState("");
  const [selectedHomeIds, setSelectedHomeIds] = useState<Set<string>>(new Set());
  const [assetsByHome, setAssetsByHome] = useState<Record<string, Asset[]>>({});
  const [loadingAssets, setLoadingAssets] = useState<Record<string, boolean>>({});
  const [selectedAssetIdsByHome, setSelectedAssetIdsByHome] = useState<Record<string, Set<string>>>({});
  const [selectedServices, setSelectedServices] = useState<Set<ServiceType>>(new Set());
  const [grantFutureAssets, setGrantFutureAssets] = useState(false);
  const [nameError, setNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [homeError, setHomeError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isCoOwner = selectedRole === "owner";

  // Only show homes the user owns
  const ownedHomes = allProperties.filter(
    (p) => p.role === "owner" || p.accessRole === "owner"
  );

  useEffect(() => {
    if (open) {
      setCurrentStep(0);
      setName(""); setEmail(""); setSelectedRole("member"); setSelectedRelation("");
      setSelectedHomeIds(new Set()); setAssetsByHome({}); setLoadingAssets({});
      setSelectedAssetIdsByHome({}); setSelectedServices(new Set());
      setGrantFutureAssets(false);
      setNameError(""); setEmailError(""); setHomeError("");
      // Pre-select first owned home
      if (ownedHomes.length >= 1) {
        const firstId = ownedHomes[0].id;
        setSelectedHomeIds(new Set([firstId]));
        loadAssetsForHome(firstId);
      }
    }
  }, [open]);

  const loadAssetsForHome = useCallback(async (homeId: string) => {
    if (assetsByHome[homeId]) return;
    setLoadingAssets((p) => ({ ...p, [homeId]: true }));
    try {
      const assets = await getHomeAssets(homeId);
      setAssetsByHome((p) => ({ ...p, [homeId]: assets }));
      setSelectedAssetIdsByHome((p) => ({
        ...p,
        [homeId]: p[homeId] ?? new Set(),
      }));
    } catch {
    } finally {
      setLoadingAssets((p) => ({ ...p, [homeId]: false }));
    }
  }, [assetsByHome]);

  const toggleHome = (homeId: string) => {
    setSelectedHomeIds((prev) => {
      const next = new Set(prev);
      if (next.has(homeId)) next.delete(homeId);
      else next.add(homeId);
      return next;
    });
    if (!assetsByHome[homeId]) loadAssetsForHome(homeId);
    setHomeError("");
  };

  const toggleAssetForHome = (homeId: string, assetId: string) => {
    setSelectedAssetIdsByHome((prev) => {
      const current = new Set(prev[homeId] ?? []);
      if (current.has(assetId)) current.delete(assetId);
      else current.add(assetId);
      return { ...prev, [homeId]: current };
    });
  };

  const selectAllAssetsForHome = (homeId: string, assets: Asset[]) => {
    setSelectedAssetIdsByHome((prev) => {
      const current = new Set(prev[homeId] ?? []);
      if (current.size === assets.length) {
        current.clear();
      } else {
        current.clear();
        assets.forEach((a) => current.add(a.id));
      }
      return { ...prev, [homeId]: current };
    });
  };

  const toggleAllServices = () => {
    setSelectedServices((prev) => {
      if (prev.size === ALL_SERVICE_TYPES.length) return new Set();
      return new Set(ALL_SERVICE_TYPES);
    });
  };

  const nextStep = () => {
    if (currentStep < TOTAL_STEPS - 1) setCurrentStep((s) => s + 1);
  };

  const prevStep = () => {
    if (currentStep > 0) setCurrentStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    const trimmedEmail = email.trim();
    const trimmedName = name.trim();
    let hasError = false;
    setNameError("");
    setEmailError("");
    setHomeError("");

    if (!trimmedName) { setNameError("Full name is required"); hasError = true; }
    if (!trimmedEmail) { setEmailError("Email is required"); hasError = true; }
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) { setEmailError("Enter a valid email address"); hasError = true; }
    if (selectedHomeIds.size === 0) { setHomeError("Select at least one home"); hasError = true; }
    if (hasError) { setCurrentStep(0); return; }

    setIsSubmitting(true);
    try {
      const promises = Array.from(selectedHomeIds).map((homeId) => {
        const assetIds = isCoOwner ? undefined
          : (Array.from(selectedAssetIdsByHome[homeId] ?? []).length > 0
              ? Array.from(selectedAssetIdsByHome[homeId] ?? []) : undefined);
        return inviteFamilyMember({
          homeId, email: trimmedEmail.toLowerCase(), role: selectedRole,
          relation: selectedRelation || undefined, inviteeName: trimmedName,
          grantFutureAssets: isCoOwner ? true : grantFutureAssets,
          assetIds: isCoOwner ? undefined : assetIds,
          serviceTypes: isCoOwner ? undefined : (selectedServices.size > 0 ? Array.from(selectedServices) : undefined),
        });
      });
      await Promise.all(promises);
      onSuccess();
      onOpenChange(false);
    } catch { } finally { setIsSubmitting(false); }
  };

  const stepTitles = ["Member Info", "Asset Access", "Service Access", "Review & Send"];

  const SectionIntro = ({ icon, title, subtitle }: { icon: React.ReactNode; title: string; subtitle: string }) => (
    <div className="flex items-start gap-3 mb-6">
      <div className="w-10 h-10 rounded-xl bg-foreground/5 flex items-center justify-center shrink-0">{icon}</div>
      <div><h3 className="text-base font-semibold text-foreground">{title}</h3><p className="text-sm text-muted-foreground mt-0.5">{subtitle}</p></div>
    </div>
  );

  /* ── Step 0: Member Info ────────────────────────────────────────────────── */
  const renderInfoStep = () => (
    <div className="space-y-5">
      <SectionIntro icon={<UserPlus className="h-5 w-5 text-foreground" />} title="Who are you inviting?" subtitle="Enter the details of the family member you'd like to invite." />
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Full Name *</label>
        <Input placeholder="Enter their full name" value={name} onChange={(e) => { setName(e.target.value); setNameError(""); }} className={nameError ? "border-red-500" : ""} />
        {nameError && <p className="text-xs text-red-500">{nameError}</p>}
      </div>
      <div className="space-y-1.5">
        <label className="text-sm font-medium">Email Address *</label>
        <Input type="email" placeholder="family@example.com" value={email} onChange={(e) => { setEmail(e.target.value); setEmailError(""); }} className={emailError ? "border-red-500" : ""} />
        {emailError && <p className="text-xs text-red-500">{emailError}</p>}
      </div>

      {/* Home Selection */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Select Homes *</label>
        <p className="text-xs text-muted-foreground">Choose which homes to share with this member</p>
        {homeError && <p className="text-xs text-red-500">{homeError}</p>}
        {propsLoading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading homes...</div>
        ) : ownedHomes.length === 0 ? (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 text-sm text-amber-700"><Info className="h-4 w-4 text-amber-500 shrink-0" />No homes found. Please create a home first.</div>
        ) : (
          <div className="space-y-2">
            {ownedHomes.map((home) => {
              const isSelected = selectedHomeIds.has(home.id);
              return (
                <button key={home.id} type="button" onClick={() => toggleHome(home.id)}
                  className={cn("flex items-center gap-3 w-full p-3 rounded-xl border text-left transition-colors", isSelected ? "border-foreground bg-secondary" : "hover:bg-secondary/50")}>
                  <div className={cn("p-2 rounded-lg", isSelected ? "bg-foreground/10" : "bg-muted")}><Home className={cn("h-5 w-5", isSelected ? "text-foreground" : "text-muted-foreground")} /></div>
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{home.address}</p><p className="text-xs text-muted-foreground">{home.city}, {home.state}</p></div>
                  <div className={cn("w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0", isSelected ? "border-foreground bg-foreground" : "border-muted-foreground")}>{isSelected && <Check className="h-3 w-3 text-background" />}</div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Role */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Role</label>
        <div className="space-y-1.5">
          {ROLE_OPTIONS.map((r) => (
            <button key={r.value} type="button" onClick={() => setSelectedRole(r.value)}
              className={cn("flex items-center gap-3 text-left w-full p-3 rounded-xl border transition-all", selectedRole === r.value ? "border-foreground bg-secondary" : "hover:bg-secondary/50")}>
              <div className={cn("mt-0.5 h-4 w-4 rounded-full border-2 shrink-0 flex items-center justify-center", selectedRole === r.value ? "border-foreground" : "border-muted-foreground")}>{selectedRole === r.value && <div className="h-2 w-2 rounded-full bg-foreground" />}</div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium">{r.label}</p><p className="text-xs text-muted-foreground">{r.desc}</p></div>
            </button>
          ))}
        </div>
      </div>

      {/* Relation */}
      <div className="space-y-2">
        <label className="text-sm font-medium">Relation <span className="text-muted-foreground font-normal">(optional)</span></label>
        <div className="flex flex-wrap gap-1.5">
          {RELATIONS.map((rel) => (
            <button key={rel} type="button" onClick={() => setSelectedRelation(selectedRelation === rel ? "" : rel)}
              className={cn("px-2.5 py-1 rounded-full text-xs font-medium border transition-colors", selectedRelation === rel ? "bg-foreground text-background border-foreground" : "text-muted-foreground hover:text-foreground")}>{rel}</button>
          ))}
        </div>
      </div>
    </div>
  );

  /* ── Step 1: Asset Access ──────────────────────────────────────────────── */
  const renderAssetStep = () => {
    const selectedHomes = ownedHomes.filter((h) => selectedHomeIds.has(h.id));
    if (isCoOwner) {
      return (
        <div className="space-y-6">
          <SectionIntro icon={<Monitor className="h-5 w-5 text-foreground" />} title="Asset Access" subtitle="Co-owners automatically get access to all current and future assets." />
          <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200"><Info className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" /><div><p className="text-sm font-medium text-blue-900">Full Access — Co-Owner</p><p className="text-xs text-blue-700 mt-0.5">Co-owners automatically get access to all current and future assets across all selected homes.</p></div></div>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <SectionIntro icon={<Monitor className="h-5 w-5 text-foreground" />} title="Asset Access" subtitle="Select which assets this member can access." />
        {selectedHomes.length === 0 ? (
          <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 text-sm text-amber-700"><Info className="h-4 w-4 text-amber-500 shrink-0" /> No homes selected. Go back to Step 1.</div>
        ) : (
          <>
            {selectedHomes.map((home, idx) => {
              const assets = assetsByHome[home.id] ?? [];
              const isLoading = loadingAssets[home.id];
              const selectedIds = selectedAssetIdsByHome[home.id] ?? new Set();
              return (
                <div key={home.id} className="space-y-2">
                  {idx > 0 && <hr className="my-4" />}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2"><Home className="h-4 w-4 text-foreground" /><p className="text-sm font-medium truncate">{home.address}</p></div>
                    {!isLoading && assets.length > 0 && (
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => selectAllAssetsForHome(home.id, assets)} className="text-xs text-blue-500 font-medium hover:underline">{selectedIds.size === assets.length ? "Deselect All" : "Select All"}</button>
                        <span className="text-xs text-muted-foreground">{selectedIds.size} of {assets.length}</span>
                      </div>
                    )}
                  </div>
                  {isLoading ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading assets...</div>
                  ) : assets.length === 0 ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground py-2"><Monitor className="h-4 w-4" /> No assets in this home yet.</div>
                  ) : (
                    <div className="space-y-1">
                      {assets.map((asset) => {
                        const isSelected = selectedIds.has(asset.id);
                        return (
                          <button key={asset.id} type="button" onClick={() => toggleAssetForHome(home.id, asset.id)}
                            className={cn("flex items-center gap-3 w-full p-2.5 rounded-lg border text-left transition-colors", isSelected ? "border-foreground bg-secondary" : "hover:bg-secondary/50")}>
                            <Monitor className={cn("h-4 w-4 shrink-0", isSelected ? "text-foreground" : "text-muted-foreground")} />
                            <div className="flex-1 min-w-0"><p className="text-sm font-medium truncate">{asset.name}</p>{(asset.brand || asset.category) && <p className="text-xs text-muted-foreground">{[asset.brand, asset.category].filter(Boolean).join(" · ")}</p>}</div>
                            <div className={cn("w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0", isSelected ? "border-foreground bg-foreground" : "border-muted-foreground")}>{isSelected && <Check className="h-3 w-3 text-background" />}</div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            <div className="flex items-start gap-3 p-4 rounded-xl bg-secondary/30">
              <div className="flex-1"><p className="text-sm font-medium">Auto-grant future assets</p><p className="text-xs text-muted-foreground mt-0.5">Automatically grant access when new assets are added to these homes.</p></div>
              <button type="button" onClick={() => setGrantFutureAssets((v) => !v)}
                className={cn("relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors", grantFutureAssets ? "bg-foreground" : "bg-muted-foreground/30")}>
                <span className={cn("inline-block h-5 w-5 rounded-full bg-background shadow-sm transition-transform", grantFutureAssets ? "translate-x-5" : "translate-x-0.5")} />
              </button>
            </div>
          </>
        )}
      </div>
    );
  };

  /* ── Step 2: Service Access ────────────────────────────────────────────── */
  const renderServiceStep = () => {
    if (isCoOwner) {
      return (
        <div className="space-y-6">
          <SectionIntro icon={<Wrench className="h-5 w-5 text-foreground" />} title="Service Access" subtitle="Co-owners get full access to all services and modules." />
          <div className="flex items-start gap-3 p-4 rounded-xl bg-blue-50 border border-blue-200"><Info className="h-5 w-5 text-blue-500 mt-0.5 shrink-0" /><div><p className="text-sm font-medium text-blue-900">Full Access — Co-Owner</p><p className="text-xs text-blue-700 mt-0.5">Co-owners get full access to all services.</p></div></div>
        </div>
      );
    }
    return (
      <div className="space-y-6">
        <SectionIntro icon={<Wrench className="h-5 w-5 text-foreground" />} title="Service Access" subtitle="Select which services this member can use." />
        <div className="flex items-center justify-between mb-2">
          <button type="button" onClick={toggleAllServices} className="text-xs text-blue-500 font-medium hover:underline">{selectedServices.size === ALL_SERVICE_TYPES.length ? "Deselect All" : "Select All"}</button>
          <span className="text-xs text-muted-foreground">{selectedServices.size} of {ALL_SERVICE_TYPES.length} selected</span>
        </div>
        <div className="space-y-2">
          {ALL_SERVICE_TYPES.map((type) => {
            const isSelected = selectedServices.has(type);
            return (
              <button key={type} type="button" onClick={() => { setSelectedServices((prev) => { const next = new Set(prev); if (next.has(type)) next.delete(type); else next.add(type); return next; }); }}
                className={cn("flex items-center gap-3 w-full p-3 rounded-lg border text-left transition-colors", isSelected ? "border-foreground bg-secondary" : "hover:bg-secondary/50")}>
                <div className={cn("p-2 rounded-lg", isSelected ? "bg-foreground/10" : "bg-muted")}><span className={isSelected ? "text-foreground" : "text-muted-foreground"}>{SERVICE_ICONS[type]}</span></div>
                <div className="flex-1 min-w-0"><p className="text-sm font-medium">{SERVICE_TYPE_LABELS[type]}</p><p className="text-xs text-muted-foreground">{SERVICE_DESCRIPTIONS[type]}</p></div>
                <div className={cn("w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0", isSelected ? "border-foreground bg-foreground" : "border-muted-foreground")}>{isSelected && <Check className="h-3 w-3 text-background" />}</div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  /* ── Step 3: Review & Send ─────────────────────────────────────────────── */
  const renderReviewStep = () => {
    const selectedHomes = ownedHomes.filter((h) => selectedHomeIds.has(h.id));
    const ReviewRow = ({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) => (
      <div className="flex items-start gap-3 py-2.5"><span className="text-foreground mt-0.5 shrink-0">{icon}</span><span className="text-xs text-muted-foreground w-16 shrink-0">{label}</span><span className="text-sm font-semibold">{value}</span></div>
    );
    return (
      <div className="space-y-6">
        <SectionIntro icon={<Check className="h-5 w-5 text-foreground" />} title="Review & Send" subtitle="Review the invitation details before sending." />
        <div className="rounded-xl border p-4 space-y-1">
          {name.trim() && <><ReviewRow icon={<User className="h-4 w-4" />} label="Name" value={name.trim()} /><hr /></>}
          <ReviewRow icon={<Mail className="h-4 w-4" />} label="Email" value={email.trim() || "—"} /><hr />
          <ReviewRow icon={<Shield className="h-4 w-4" />} label="Role" value={roleLabel(selectedRole)} />
          {selectedRelation && <><hr /><ReviewRow icon={<User className="h-4 w-4" />} label="Relation" value={selectedRelation} /></>}
          <hr />
          <div className="flex items-start gap-3 py-2.5"><Home className="h-4 w-4 text-foreground mt-0.5 shrink-0" /><span className="text-xs text-muted-foreground w-16 shrink-0">Homes</span><span className="text-sm font-semibold">{selectedHomes.length} home{selectedHomes.length !== 1 ? "s" : ""}</span></div>
          {!isCoOwner && selectedHomes.map((home) => {
            const assetIds = selectedAssetIdsByHome[home.id] ?? new Set();
            const assets = assetsByHome[home.id] ?? [];
            const selectedAssets = assets.filter((a) => assetIds.has(a.id));
            return (
              <div key={home.id} className="ml-16 p-2 border rounded-lg text-xs space-y-1">
                <div className="flex items-center gap-1.5"><Home className="h-3 w-3 text-muted-foreground" /><span className="font-medium">{home.address}</span></div>
                <p className="text-muted-foreground ml-[1.125rem]">{selectedAssets.length === 0 ? "No assets selected" : `${selectedAssets.length} asset${selectedAssets.length !== 1 ? "s" : ""}`}</p>
                {selectedAssets.length > 0 && <div className="flex flex-wrap gap-1 ml-[1.125rem]">{selectedAssets.slice(0, 5).map((a) => (<span key={a.id} className="px-1.5 py-0.5 rounded bg-foreground/10 text-foreground text-[10px] font-medium">{a.name}</span>))}{selectedAssets.length > 5 && <span className="text-[10px] text-muted-foreground">+{selectedAssets.length - 5} more</span>}</div>}
              </div>
            );
          })}
          <hr />
          <div className="flex items-start gap-3 py-2.5"><Wrench className="h-4 w-4 text-foreground mt-0.5 shrink-0" /><span className="text-xs text-muted-foreground w-16 shrink-0">Services</span><span className="text-sm font-semibold">{isCoOwner ? "All services" : selectedServices.size === 0 ? "None" : `${selectedServices.size} service${selectedServices.size !== 1 ? "s" : ""}`}</span></div>
          {!isCoOwner && selectedServices.size > 0 && <div className="flex flex-wrap gap-1 ml-16">{Array.from(selectedServices).map((t) => (<span key={t} className="px-1.5 py-0.5 rounded bg-foreground/10 text-foreground text-[10px] font-medium">{SERVICE_TYPE_LABELS[t]}</span>))}</div>}
          <hr />
          <ReviewRow icon={<Clock className="h-4 w-4" />} label="Future" value={isCoOwner ? "Auto" : (grantFutureAssets ? "Auto-grant on" : "Manual only")} />
        </div>
        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 text-xs text-blue-700"><Info className="h-4 w-4 text-blue-500 mt-0.5 shrink-0" /><span>An invitation email will be sent. The invite expires in 7 days.</span></div>
      </div>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) { case 0: return renderInfoStep(); case 1: return renderAssetStep(); case 2: return renderServiceStep(); case 3: return renderReviewStep(); default: return null; }
  };

  const canAdvance = currentStep === 0 ? email.trim().length > 0 && selectedHomeIds.size > 0 : true;

  return (
    <Sheet open={open} onOpenChange={onOpenChange} side="right">
      <SheetContent side="right" className="w-full sm:max-w-lg [&>button]:hidden p-0">
        <div className="sticky top-0 bg-background z-10 border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-foreground/5 rounded-lg"><UserPlus className="h-5 w-5 text-foreground" /></div>
              <div><h2 className="text-lg font-semibold text-foreground leading-none">Invite Family Member</h2><p className="text-xs text-muted-foreground mt-1">Step {currentStep + 1} of {TOTAL_STEPS} — {stepTitles[currentStep]}</p></div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => onOpenChange(false)} className="h-8 w-8"><X className="h-5 w-5" /></Button>
          </div>
          <div className="flex gap-1.5 mt-4">
            {Array.from({ length: TOTAL_STEPS }).map((_, i) => (
              <div key={i} className={cn("h-1 flex-1 rounded-full transition-colors", i < currentStep ? "bg-green-500" : i === currentStep ? "bg-foreground" : "bg-muted-foreground/20")} />
            ))}
          </div>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5">{renderStepContent()}</div>
        <div className="shrink-0 border-t px-6 py-4 flex items-center justify-between gap-3">
          {currentStep > 0 ? (
            <Button variant="outline" onClick={prevStep} className="flex items-center gap-1"><ChevronLeft className="h-4 w-4" /> Back</Button>
          ) : (
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          )}
          {currentStep < TOTAL_STEPS - 1 ? (
            <Button onClick={nextStep} disabled={!canAdvance} className="flex items-center gap-1">Next <ArrowRight className="h-4 w-4" /></Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting} className="flex items-center gap-2">
              {isSubmitting ? (<><Loader2 className="h-4 w-4 animate-spin" /> Sending...</>) : (<><Send className="h-4 w-4" /> Send Invitation</>)}
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
